import React, { useState, useEffect } from 'react';
import { COLORS } from '../../utils/constants';
import { tradingAPI } from '../../services/api';

import { Wallet, RefreshCw, ArrowUpRight, ArrowDownRight, Coins } from 'lucide-react';

// Helper functions for formatting

// ============== HELPER FUNCTIONS ==============
const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value || 0);
};

const formatQuantity = (qty) => {
  if (qty >= 1000) return qty.toFixed(2);
  if (qty >= 1) return qty.toFixed(4);
  return qty.toFixed(6);
};

// ============== MOBILE HEADER ==============
const MobileHeader = ({ holdings, totalValue, totalPnL, totalPnLPercent, refreshing, handleRefresh }) => (
  <div style={{
    padding: '12px 16px',
    borderBottom: `1px solid ${COLORS.border}`,
    backgroundColor: COLORS.background,
  }}>
    <div style={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center',
      marginBottom: '12px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Wallet size={18} color={COLORS.primary} />
        <span style={{ color: COLORS.text, fontWeight: '700', fontSize: '15px' }}>
          Spot Assets
        </span>
        <span style={{ 
          backgroundColor: COLORS.primary, 
          color: '#000', 
          padding: '2px 8px', 
          borderRadius: '10px',
          fontSize: '11px',
          fontWeight: '700'
        }}>
          {holdings.length}
        </span>
      </div>
      <button
        onClick={handleRefresh}
        disabled={refreshing}
        style={{
          background: 'none',
          border: `1px solid ${COLORS.border}`,
          borderRadius: '6px',
          padding: '8px',
          cursor: 'pointer',
          color: COLORS.textSecondary,
        }}
      >
        <RefreshCw size={14} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
      </button>
    </div>
    
    <div style={{ 
      display: 'flex', 
      justifyContent: 'space-between',
      backgroundColor: '#1e232950',
      borderRadius: '8px',
      padding: '10px 12px',
    }}>
      <div>
        <div style={{ color: COLORS.textSecondary, fontSize: '11px', marginBottom: '2px' }}>
          Total Value
        </div>
        <div style={{ color: COLORS.text, fontWeight: '700', fontSize: '16px', fontFamily: 'monospace' }}>
          {formatCurrency(totalValue)}
        </div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <div style={{ color: COLORS.textSecondary, fontSize: '11px', marginBottom: '2px' }}>
          Total P&L
        </div>
        <div style={{ 
          color: totalPnL >= 0 ? COLORS.success : COLORS.danger,
          fontWeight: '700',
          fontSize: '16px',
          fontFamily: 'monospace',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '4px'
        }}>
          {totalPnL >= 0 ? '+' : ''}{formatCurrency(totalPnL)}
          <span style={{ fontSize: '12px', opacity: 0.8 }}>
            ({totalPnLPercent >= 0 ? '+' : ''}{totalPnLPercent.toFixed(2)}%)
          </span>
        </div>
      </div>
    </div>
  </div>
);

// ============== DESKTOP HEADER ==============
const DesktopHeader = ({ holdings, totalValue, totalPnL, totalPnLPercent, refreshing, handleRefresh }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 20px',
    borderBottom: '1px solid #252930',
    backgroundColor: '#1e2329',
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div style={{
        width: '32px',
        height: '32px',
        borderRadius: '8px',
        background: 'linear-gradient(135deg, #f5bc00 0%, #d4a300 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Wallet size={18} color="#0b0e11" />
      </div>
      <div>
        <span style={{ color: '#eaecef', fontWeight: '700', fontSize: '15px', display: 'block' }}>
          Спот Активы
        </span>
        <span style={{ color: '#5e6673', fontSize: '11px' }}>
          {holdings.length} актив{holdings.length === 1 ? '' : holdings.length < 5 ? 'а' : 'ов'}
        </span>
      </div>
    </div>

    <div style={{ display: 'flex', alignItems: 'center', gap: '32px' }}>
      <div style={{ textAlign: 'right' }}>
        <div style={{ color: '#5e6673', fontSize: '11px', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Стоимость
        </div>
        <div style={{ color: '#eaecef', fontWeight: '700', fontSize: '16px', fontFamily: 'monospace' }}>
          {formatCurrency(totalValue)}
        </div>
      </div>
      
      <div style={{ textAlign: 'right' }}>
        <div style={{ color: '#5e6673', fontSize: '11px', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Нереализ. PnL
        </div>
        <div style={{ 
          color: totalPnL >= 0 ? '#0ecb81' : '#f6465d',
          fontWeight: '700',
          fontSize: '16px',
          fontFamily: 'monospace',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          justifyContent: 'flex-end',
        }}>
          {totalPnL >= 0 ? '+' : ''}{formatCurrency(totalPnL)}
          <span style={{ 
            fontSize: '12px', 
            padding: '2px 6px', 
            borderRadius: '4px',
            backgroundColor: totalPnL >= 0 ? '#0ecb8115' : '#f6465d15',
          }}>
            {totalPnLPercent >= 0 ? '+' : ''}{totalPnLPercent.toFixed(2)}%
          </span>
        </div>
      </div>

      <button
        onClick={handleRefresh}
        disabled={refreshing}
        style={{
          background: '#252930',
          border: 'none',
          borderRadius: '8px',
          padding: '10px',
          cursor: 'pointer',
          color: '#848e9c',
          transition: 'all 0.2s ease',
        }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2b3139'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#252930'}
      >
        <RefreshCw size={16} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
      </button>
    </div>
  </div>
);

// ============== MOBILE TABLE HEADER ==============
const MobileTableHeader = () => (
  <div style={{
    display: 'grid',
    gridTemplateColumns: '1fr 80px 70px',
    padding: '10px 16px',
    borderBottom: `1px solid ${COLORS.border}`,
    backgroundColor: COLORS.background,
    gap: '8px',
  }}>
    <span style={{ color: COLORS.textSecondary, fontSize: '11px', fontWeight: '600' }}>Asset</span>
    <span style={{ color: COLORS.textSecondary, fontSize: '11px', fontWeight: '600', textAlign: 'right' }}>Value</span>
    <span style={{ color: COLORS.textSecondary, fontSize: '11px', fontWeight: '600', textAlign: 'right' }}>P&L</span>
  </div>
);

// ============== DESKTOP TABLE HEADER ==============
const DesktopTableHeader = () => (
  <div style={{
    display: 'grid',
    gridTemplateColumns: '1.5fr 0.9fr 0.9fr 0.9fr 1fr 160px',
    padding: '10px 20px',
    borderBottom: '1px solid #252930',
    backgroundColor: '#1e2329',
  }}>
    <span style={{ color: '#5e6673', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Актив</span>
    <span style={{ color: '#5e6673', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'right' }}>Кол-во</span>
    <span style={{ color: '#5e6673', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'right' }}>Ср. цена</span>
    <span style={{ color: '#5e6673', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'right' }}>Стоимость</span>
    <span style={{ color: '#5e6673', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'right' }}>PnL</span>
    <span style={{ color: '#5e6673', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'right' }}>Действия</span>
  </div>
);

// ============== MOBILE ROW ==============
const MobileRow = ({ holding, isSelected, onSelectAsset }) => {
  const pnlPercent = holding.pnl_percent || 0;
  
  return (
    <div
      onClick={() => onSelectAsset && onSelectAsset(`${holding.symbol}USDT`)}
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 80px 70px',
        padding: '12px 16px',
        borderBottom: `1px solid ${COLORS.border}20`,
        cursor: 'pointer',
        backgroundColor: isSelected ? `${COLORS.primary}10` : 'transparent',
        gap: '8px',
        alignItems: 'center',
      }}
    >
      {/* Asset */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
        <div style={{
          width: '32px',
          height: '32px',
          minWidth: '32px',
          borderRadius: '50%',
          backgroundColor: `${COLORS.primary}20`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '11px',
          fontWeight: '700',
          color: COLORS.primary,
        }}>
          {holding.symbol.slice(0, 2)}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ color: COLORS.text, fontWeight: '600', fontSize: '13px' }}>
            {holding.symbol}
          </div>
          <div style={{ color: COLORS.textSecondary, fontSize: '11px', fontFamily: 'monospace' }}>
            {formatQuantity(holding.quantity)}
          </div>
        </div>
      </div>
      
      {/* Value */}
      <div style={{ textAlign: 'right' }}>
        <span style={{ 
          color: COLORS.text, 
          fontSize: '12px', 
          fontFamily: 'monospace',
          fontWeight: '600',
        }}>
          {formatCurrency(holding.current_value)}
        </span>
      </div>
      
      {/* P&L */}
      <div style={{ textAlign: 'right' }}>
        <div style={{ 
          color: holding.pnl >= 0 ? COLORS.success : COLORS.danger,
          fontSize: '12px',
          fontWeight: '600',
          fontFamily: 'monospace',
        }}>
          {holding.pnl >= 0 ? '+' : ''}{holding.pnl?.toFixed(2) || '0.00'}
        </div>
        <div style={{ 
          color: pnlPercent >= 0 ? COLORS.success : COLORS.danger,
          fontSize: '10px',
          opacity: 0.8,
        }}>
          {pnlPercent >= 0 ? '+' : ''}{pnlPercent.toFixed(2)}%
        </div>
      </div>
    </div>
  );
};

// ============== DESKTOP ROW ==============
const DesktopRow = ({ holding, isSelected, onSelectAsset, onSellAsset, onBuyAsset, index }) => {
  const pnlPercent = holding.pnl_percent || 0;
  
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1.5fr 0.9fr 0.9fr 0.9fr 1fr 160px',
        padding: '14px 20px',
        borderBottom: '1px solid #252930',
        backgroundColor: isSelected ? '#2b3139' : (index % 2 === 0 ? '#161a1e' : '#1a1e23'),
        transition: 'all 0.2s ease',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
      onMouseEnter={(e) => {
        if (!isSelected) e.currentTarget.style.backgroundColor = '#1e2329';
      }}
      onMouseLeave={(e) => {
        if (!isSelected) e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#161a1e' : '#1a1e23';
      }}
    >
      {/* Asset */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{
          width: '36px',
          height: '36px',
          borderRadius: '50%',
          background: `linear-gradient(135deg, #f5bc00 0%, #d4a300 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '12px',
          fontWeight: '700',
          color: '#0b0e11',
          boxShadow: '0 2px 8px rgba(245, 188, 0, 0.25)',
          flexShrink: 0,
        }}>
          {holding.symbol.slice(0, 2)}
        </div>
        <div>
          <div style={{ color: '#eaecef', fontWeight: '700', fontSize: '14px', marginBottom: '2px' }}>
            {holding.symbol}
          </div>
          <div style={{ color: '#5e6673', fontSize: '11px' }}>
            {holding.symbol}/USDT
          </div>
        </div>
      </div>
      
      {/* Quantity */}
      <div style={{ textAlign: 'right', alignSelf: 'center' }}>
        <span style={{ color: '#eaecef', fontSize: '13px', fontFamily: 'monospace', fontWeight: '500' }}>
          {formatQuantity(holding.quantity)}
        </span>
      </div>
      
      {/* Avg Price */}
      <div style={{ textAlign: 'right', alignSelf: 'center' }}>
        <span style={{ color: '#eaecef', fontSize: '13px', fontFamily: 'monospace' }}>
          ${holding.avg_buy_price?.toFixed(2) || '0.00'}
        </span>
      </div>
      
      {/* Value */}
      <div style={{ textAlign: 'right', alignSelf: 'center' }}>
        <span style={{ color: '#eaecef', fontSize: '13px', fontFamily: 'monospace', fontWeight: '600' }}>
          {formatCurrency(holding.current_value)}
        </span>
      </div>
      
      {/* P&L */}
      <div style={{ textAlign: 'right', alignSelf: 'center', paddingRight: '12px' }}>
        <div style={{ 
          color: holding.pnl >= 0 ? '#0ecb81' : '#f6465d',
          fontSize: '13px',
          fontWeight: '700',
          fontFamily: 'monospace',
        }}>
          {holding.pnl >= 0 ? '+' : ''}{formatCurrency(holding.pnl)}
        </div>
        <div style={{ 
          color: pnlPercent >= 0 ? '#0ecb81' : '#f6465d',
          fontSize: '11px',
        }}>
          ({pnlPercent >= 0 ? '+' : ''}{pnlPercent.toFixed(2)}%)
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '6px', alignSelf: 'center', justifyContent: 'flex-end' }}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onBuyAsset && onBuyAsset(holding);
          }}
          style={{
            padding: '6px 12px',
            background: 'linear-gradient(180deg, #0ecb81 0%, #0ba369 100%)',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => e.currentTarget.style.filter = 'brightness(1.1)'}
          onMouseLeave={(e) => e.currentTarget.style.filter = 'brightness(1)'}
        >
          Купить
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onSellAsset && onSellAsset(holding);
          }}
          style={{
            padding: '6px 12px',
            backgroundColor: 'transparent',
            color: '#f6465d',
            border: '1px solid #f6465d',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#f6465d';
            e.currentTarget.style.color = '#fff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = '#f6465d';
          }}
        >
          Продать
        </button>
      </div>
    </div>
  );
};

// ============== EMPTY STATE ==============
const EmptyState = ({ isMobile }) => (
  <div style={{ 
    display: 'flex', 
    flexDirection: 'column',
    alignItems: 'center', 
    justifyContent: 'center', 
    height: '100%',
    color: COLORS.textSecondary,
    gap: '16px',
    padding: isMobile ? '30px 16px' : '40px'
  }}>
    <Coins size={isMobile ? 40 : 48} style={{ opacity: 0.4 }} />
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: isMobile ? '14px' : '16px', marginBottom: '8px', color: COLORS.text }}>
        No spot assets yet
      </div>
      <div style={{ fontSize: isMobile ? '12px' : '14px', opacity: 0.7 }}>
        Buy crypto in Spot mode to see your portfolio here
      </div>
    </div>
  </div>
);

// ============== MAIN COMPONENT ==============
const SpotAssetsPanel = ({ onSelectAsset, currentSymbol, onSellAsset, onBuyAsset, refreshTrigger }) => {
  const [holdings, setHoldings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [totalValue, setTotalValue] = useState(0);
  const [totalPnL, setTotalPnL] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadHoldings();
    const interval = setInterval(loadHoldings, 30000);
    return () => clearInterval(interval);
  }, []);

  // Refresh when refreshTrigger changes (after buy/sell)
  useEffect(() => {
    if (refreshTrigger > 0) {
      loadHoldings();
    }
  }, [refreshTrigger]);

  const loadHoldings = async () => {
    try {
      const response = await tradingAPI.getHoldings();
      const data = response.data || [];
      setHoldings(data);
      
      const total = data.reduce((sum, h) => sum + (h.current_value || 0), 0);
      const pnl = data.reduce((sum, h) => sum + (h.pnl || 0), 0);
      setTotalValue(total);
      setTotalPnL(pnl);
    } catch (error) {
      console.error('Failed to load holdings:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadHoldings();
  };

  const baseAsset = currentSymbol?.replace('USDT', '') || 'BTC';
  const totalPnLPercent = totalValue > 0 ? (totalPnL / (totalValue - totalPnL)) * 100 : 0;

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100%',
        color: '#848e9c',
        backgroundColor: '#0b0e11',
      }}>
        Загрузка активов...
      </div>
    );
  }

  const headerProps = { holdings, totalValue, totalPnL, totalPnLPercent, refreshing, handleRefresh };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: '#0b0e11' }}>
      {/* Header */}
      {isMobile ? <MobileHeader {...headerProps} /> : <DesktopHeader {...headerProps} />}

      {/* Table Header */}
      {isMobile ? <MobileTableHeader /> : <DesktopTableHeader />}

      {/* Assets List */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {holdings.length === 0 ? (
          <EmptyState isMobile={isMobile} />
        ) : (
          holdings.map((holding, index) => {
            const isSelected = holding.symbol === baseAsset;
            
            return isMobile ? (
              <MobileRow 
                key={holding.id} 
                holding={holding} 
                isSelected={isSelected} 
                onSelectAsset={onSelectAsset} 
              />
            ) : (
              <DesktopRow 
                key={holding.id} 
                holding={holding} 
                isSelected={isSelected} 
                onSelectAsset={onSelectAsset}
                onSellAsset={onSellAsset}
                onBuyAsset={onBuyAsset}
                index={index}
              />
            );
          })
        )}
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default SpotAssetsPanel;
